import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SfAccountSnapshot } from '@/types';
import { api } from '@/lib/api';
import { Loader2, Calendar, AlertTriangle } from 'lucide-react';

interface ScheduleCancellationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: SfAccountSnapshot;
  isChangingDate: boolean;
  onSuccess: () => void;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

function formatDateForDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function ScheduleCancellationModal({
  open,
  onOpenChange,
  account,
  isChangingDate,
  onSuccess,
}: ScheduleCancellationModalProps) {
  const [endDate, setEndDate] = useState(account.ai_contract_end || getTodayString());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const today = getTodayString();
  const minDate = account.ai_contract_start || today;

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await api.scheduleCancellation(account.sf_account_id, endDate);

      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onSuccess();
        }, 2000);
      } else {
        setError(result.error || 'Failed to schedule cancellation');
      }
    } catch (err: unknown) {
      // Handle ApiError from the API client
      const apiError = err as { error?: string };
      const errorMessage = apiError?.error || (err instanceof Error ? err.message : 'An unexpected error occurred');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setSuccess(false);
      setEndDate(account.ai_contract_end || getTodayString());
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-abby-primary" />
            {isChangingDate ? 'Change End Date' : 'Schedule Cancellation'}
          </DialogTitle>
          <DialogDescription>
            {isChangingDate
              ? 'Change the scheduled end date for this account.'
              : 'Schedule the cancellation of this account. Service will remain active through end of day on the selected date.'}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm text-gray-600">
              {isChangingDate ? 'End date updated successfully!' : 'Cancellation scheduled successfully!'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Service will remain active through end of day {formatDateForDisplay(endDate)}.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  min={minDate < today ? today : minDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500">
                  Service will remain active through end of day on this date.
                </p>
              </div>

              {endDate && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">Confirm cancellation</p>
                      <p className="text-amber-700 mt-1">
                        This will {isChangingDate ? 'update the end date to' : 'schedule cancellation for'}{' '}
                        <strong>{formatDateForDisplay(endDate)}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !endDate}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {isChangingDate ? 'Updating...' : 'Scheduling...'}
                  </>
                ) : isChangingDate ? (
                  'Update End Date'
                ) : (
                  'Schedule Cancellation'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
